import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { campaignCategoryService, campaignService } from '../services/api';
import SimplePageBanner from '../components/SimplePageBanner';
import { bodyTextSx, primaryButtonSx, cardSx, BRAND, sectionPaddingSx } from '../theme/pageStyles';

const SubmitCampaignPage = () => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    target_amount: '',
    start_date: '',
    end_date: '',
    category_id: '',
    submitter_name: '',
    submitter_email: '',
    submitter_phone: '',
  });

  useEffect(() => {
    campaignCategoryService
      .getAll()
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        setCategories(Array.isArray(list) ? list : []);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoadingCategories(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.category_id) {
      setError('Please select a category for your campaign.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value != null) fd.append(key, value);
      });
      if (selectedImage) fd.append('image', selectedImage);
      await campaignService.submitCampaign(fd);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to submit campaign');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Create impact"
        title="Start a Campaign"
        description="Submit your campaign idea under an existing category. Our team will review it before it goes live."
      />

      <Box sx={{ backgroundColor: '#FAF9F6', ...sectionPaddingSx }}>
        <Container maxWidth="md">
          <Card sx={cardSx} elevation={0}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {submitted ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ ...bodyTextSx, fontWeight: 600, color: BRAND.green, mb: 2 }}>
                    Campaign submitted successfully
                  </Typography>
                  <Typography sx={bodyTextSx}>
                    Thank you. An administrator will review your campaign. Once approved, it will appear on our
                    campaigns page.
                  </Typography>
                </Box>
              ) : loadingCategories ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: BRAND.green }} />
                </Box>
              ) : (
                <form onSubmit={handleSubmit}>
                  <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: 20, mb: 3 }}>
                    Campaign details
                  </Typography>

                  {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  ) : null}

                  <TextField
                    select
                    fullWidth
                    required
                    label="Category *"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    margin="normal"
                    disabled={categories.length === 0}
                    helperText={categories.length === 0 ? 'No categories available yet.' : ''}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    required
                    label="Campaign title *"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    margin="normal"
                  />

                  <TextField
                    fullWidth
                    required
                    label="Short description *"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    margin="normal"
                    multiline
                    rows={3}
                  />

                  <TextField
                    fullWidth
                    label="Full story / details"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    margin="normal"
                    multiline
                    rows={5}
                  />

                  <TextField
                    fullWidth
                    label="Fundraising goal (₹)"
                    name="target_amount"
                    type="number"
                    value={formData.target_amount}
                    onChange={handleChange}
                    margin="normal"
                    inputProps={{ min: 0 }}
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
                    <TextField
                      label="Start date"
                      name="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End date"
                      name="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Button variant="outlined" component="label" startIcon={<CloudUpload />} sx={{ textTransform: 'none' }}>
                      Upload campaign image
                      <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                    </Button>
                    {imagePreview && (
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{ display: 'block', mt: 2, maxWidth: 280, maxHeight: 180, borderRadius: 2, objectFit: 'cover' }}
                      />
                    )}
                  </Box>

                  <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: 18, mt: 3, mb: 1 }}>
                    Your contact information
                  </Typography>

                  <TextField
                    fullWidth
                    required
                    label="Your name *"
                    name="submitter_name"
                    value={formData.submitter_name}
                    onChange={handleChange}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    required
                    label="Email *"
                    name="submitter_email"
                    type="email"
                    value={formData.submitter_email}
                    onChange={handleChange}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    required
                    label="Phone *"
                    name="submitter_phone"
                    value={formData.submitter_phone}
                    onChange={handleChange}
                    margin="normal"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting || categories.length === 0}
                    sx={{ ...primaryButtonSx, mt: 4 }}
                  >
                    {submitting ? 'Submitting…' : 'Submit for review'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default SubmitCampaignPage;
